import { useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { rupiah, tanggalID, todayISO, toDateInput } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { usePabrik } from "@/context/PabrikContext";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, FileText, Send, MessageCircle, Search } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";
import AkunSelect from "@/components/app/AkunSelect";

const empty = {
  tanggal: todayISO(), no_invoice_num: "001", kategori_id: "", kategori_nama: "",
  nama_barang: "", nama_pembeli: "", no_wa_pembeli: "", email_pembeli: "",
  harga: 0, diskon_persen: 0, pajak_persen: 0, keterangan: "", status_pembayaran: "lunas",
  kg_terjual: 0, harga_per_kg: 0, dp_amount: 0,
};

export default function Pemasukan() {
  const { user } = useAuth();
  const isArang = user?.workspace_type === "arang";
  const pabrik = usePabrik();
  const targets = (pabrik.targetsBriket || []).filter((t) => t.id !== pabrik.current?.id);
  const canInternal = isArang && pabrik.enabled && pabrik.current?.jenis !== "briket" && targets.length > 0;
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [kategori, setKategori] = useState([]);
  const [pembeli, setPembeli] = useState([]);
  const [settings, setSettings] = useState({is_pkp:false, ppn_persen:11});
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [produkList, setProdukList] = useState([]);
  const load = async () => {
    const [a, k, p, s] = await Promise.all([
      api.get("/pemasukan"),
      api.get("/kategori", { params: { tipe: "pemasukan" } }),
      api.get("/pembeli"),
      api.get("/settings"),
    ]);
    setItems(a.data); setKategori(k.data); setPembeli(p.data); setSettings(s.data);
    if (isArang) {
      try { const pr = await api.get("/kategori", { params: { tipe: "produk" } }); setProdukList(pr.data); } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = async () => {
    setEditId(null);
    const defaultPajak = settings.is_pkp ? Number(settings.ppn_persen || 0) : 0;
    try {
      const r = await api.get("/pemasukan/next-invoice");
      setForm({ ...empty, no_invoice_num: r.data.next_num, tanggal: todayISO(), pajak_persen: defaultPajak, produk_id: produkList[0]?.id || "" });
    } catch { setForm({ ...empty, pajak_persen: defaultPajak, produk_id: produkList[0]?.id || "" }); }
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditId(it.id);
    setForm({ ...empty, ...it, tanggal: toDateInput(it.tanggal) || todayISO() });
    setOpen(true);
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });

  // Auto-computed subtotal
  const baseHarga = (() => {
    if (isArang && Number(form.kg_terjual) > 0 && Number(form.harga_per_kg) > 0) {
      return Number(form.kg_terjual) * Number(form.harga_per_kg);
    }
    return Number(form.harga || 0);
  })();
  const total = (() => {
    const sub = baseHarga - baseHarga * Number(form.diskon_persen || 0) / 100;
    return Math.round(sub + sub * Number(form.pajak_persen || 0) / 100);
  })();
  const sisaTagihan = (() => {
    const st = (form.status_pembayaran || "lunas").toLowerCase();
    if (st === "lunas") return 0;
    if (st === "dp") return Math.max(0, total - Number(form.dp_amount || 0));
    return total;
  })();

  const save = async () => {
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    if (!form.no_invoice_num?.toString().trim()) { toast.error("No. Invoice wajib diisi"); return; }
    if (!form.nama_barang?.trim()) { toast.error("Nama barang/jasa wajib diisi"); return; }
    if (!form.nama_pembeli?.trim()) { toast.error("Nama pembeli wajib diisi"); return; }
    if (isArang) {
      if (!(Number(form.kg_terjual) > 0)) { toast.error("Kg Terjual wajib diisi (lebih dari 0)"); return; }
      if (!(Number(form.harga_per_kg) > 0)) { toast.error("Harga per Kg wajib diisi (lebih dari 0)"); return; }
    } else if (!(Number(form.harga) > 0)) {
      toast.error("Harga wajib diisi (lebih dari 0)"); return;
    }
    const payload = {
      ...form,
      harga: Number(isArang && Number(form.kg_terjual)>0 && Number(form.harga_per_kg)>0 ? baseHarga : (form.harga || 0)),
      diskon_persen: Number(form.diskon_persen),
      pajak_persen: Number(form.pajak_persen),
      kg_terjual: Number(form.kg_terjual || 0),
      harga_per_kg: Number(form.harga_per_kg || 0),
      dp_amount: Number(form.dp_amount || 0),
      produk_id: isArang ? (form.produk_id || produkList[0]?.id || null) : null,
      ke_pabrik_id: canInternal && form.ke_pabrik_id ? form.ke_pabrik_id : null,
    };
    try {
      if (editId) await api.put(`/pemasukan/${editId}`, payload);
      else await api.post("/pemasukan", payload);
      toast.success(editId ? "Data diperbarui" : "Pemasukan tersimpan");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delOne = async (id) => {
    if (!window.confirm("Hapus data ini?")) return;
    await api.delete(`/pemasukan/${id}`); toast.success("Terhapus"); load();
  };

  const toggle = (id) => { const s=new Set(selected); s.has(id)?s.delete(id):s.add(id); setSelected(s); };
  const toggleAll = () => selected.size===filtered.length ? setSelected(new Set()) : setSelected(new Set(filtered.map(i=>i.id)));

  const bulkDelete = async () => {
    if (!selected.size || !window.confirm(`Hapus ${selected.size} data?`)) return;
    await api.post("/pemasukan/bulk-delete", { ids: Array.from(selected) });
    setSelected(new Set()); await load(); toast.success("Terhapus");
  };

  const filtered = items.filter(it => {
    const s = q.trim().toLowerCase();
    const okQ = !s || [it.no_invoice, it.kategori_nama, it.nama_barang, it.nama_pembeli, it.tanggal]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(s));
    const okFrom = !from || (it.tanggal || "") >= from;
    const okTo = !to || (it.tanggal || "") <= to;
    return okQ && okFrom && okTo;
  });
  const { page, setPage, totalPages, paged, total: totalRows, pageSize } = usePagination(filtered, 10);

  const openInvoice = (id) => {
    const token = localStorage.getItem("khn_token");
    const url = `${API_BASE}/pemasukan/${id}/invoice-pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => window.open(URL.createObjectURL(b), "_blank"));
  };

  const sendEmail = async (it) => {
    if (!it.email_pembeli) { toast.error("Email pembeli belum diisi"); return; }
    try {
      const r = await api.post(`/pemasukan/${it.id}/send-invoice-email`);
      toast.success(r.data.message);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendWA = (it) => {
    if (!it.no_wa_pembeli) { toast.error("No. WhatsApp pembeli belum diisi"); return; }
    const total = it.total || 0;
    const brand = isArang ? "Bisnis Arang" : "HostNesia Indonesia";
    const msg = `Halo *${it.nama_pembeli}*,%0A%0ABerikut invoice dari *${brand}*:%0A%0A*No*: ${it.no_invoice}%0A*Tanggal*: ${it.tanggal}%0A*Item*: ${it.nama_barang}%0A*Total*: ${rupiah(total)}%0A%0ATerima kasih.`;
    const wa = it.no_wa_pembeli.replace(/^0/, "62").replace(/\D/g, "");
    window.open(`https://wa.me/${wa}?text=${msg}`, "_blank");
  };

  const statusBadge = (st) => {
    const s = (st || "lunas").toLowerCase();
    const map = {
      lunas: "bg-emerald-100 text-emerald-700",
      dp: "bg-blue-100 text-blue-700",
      belum: "bg-rose-100 text-rose-700",
      piutang: "bg-amber-100 text-amber-700",
    };
    return `text-[11px] px-2 py-0.5 rounded-sm ${map[s] || map.lunas}`;
  };

  return (
    <>
      <PageHeader title="Pemasukan" subtitle={isArang ? "Catat penjualan arang (Kg × Harga/Kg)." : "Catat pendapatan dari penjualan barang/jasa."}
        actions={
          <>
            {selected.size > 0 && (
              <Button variant="outline" onClick={bulkDelete} data-testid="bulk-delete-pemasukan" className="rounded-sm">
                <Trash2 size={14} className="mr-1"/> Hapus ({selected.size})
              </Button>
            )}
            <Button onClick={openNew} data-testid="add-pemasukan-btn" className="brand-btn rounded-sm">
              <Plus size={14} className="mr-1"/> Tambah
            </Button>
          </>
        }
      />
      <div className="p-4 md:p-8 space-y-4">
        <div className="card-flat p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input className="pl-9" placeholder="Cari invoice, kategori, barang, pembeli..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="pmk-search"/>
          </div>
          <div><Input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} data-testid="pmk-from"/></div>
          <div><Input type="date" value={to} onChange={(e)=>setTo(e.target.value)} data-testid="pmk-to"/></div>
        </div>

        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm" data-testid="pemasukan-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="p-3 w-10"><Checkbox checked={filtered.length>0 && selected.size===filtered.length} onCheckedChange={toggleAll}/></th>
                <th className="p-3">Tanggal</th>
                <th className="p-3">No. Invoice</th>
                {isArang && <th className="p-3">Produk</th>}
                {isArang && <th className="p-3 text-right">Kg</th>}
                {isArang && <th className="p-3 text-right">Harga/Kg</th>}
                {!isArang && <th className="p-3">Kategori</th>}
                <th className="p-3">Barang</th>
                <th className="p-3 hide-mobile">Pembeli</th>
                <th className="p-3 text-right">Total</th>
                {isArang && <th className="p-3 text-right hide-mobile">Sisa</th>}
                <th className="p-3 hide-mobile">Status</th>
                <th className="p-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length===0 && <tr><td colSpan={isArang?12:9} className="text-center py-10 text-slate-400">Tidak ada data.</td></tr>}
              {paged.map(it => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3"><Checkbox checked={selected.has(it.id)} onCheckedChange={()=>toggle(it.id)}/></td>
                  <td className="p-3 whitespace-nowrap">{tanggalID(it.tanggal)}</td>
                  <td className="p-3 font-mono text-[12px]">{it.no_invoice}</td>
                  {isArang && <td className="p-3"><span className="text-[11px] px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700 whitespace-nowrap" data-testid={`pmk-produk-${it.id}`}>{it.produk_nama || "-"}</span></td>}
                  {isArang && <td className="p-3 text-right font-mono">{Number(it.kg_terjual||0).toLocaleString("id-ID")}</td>}
                  {isArang && <td className="p-3 text-right font-mono">{rupiah(it.harga_per_kg)}</td>}
                  {!isArang && <td className="p-3">{it.kategori_nama || "-"}</td>}
                  <td className="p-3">{it.nama_barang}</td>
                  <td className="p-3 hide-mobile">{it.nama_pembeli}{it.internal && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-violet-50 text-violet-700 border border-violet-200 whitespace-nowrap" data-testid={`pmk-internal-${it.id}`}>Internal</span>}</td>
                  <td className="p-3 text-right font-mono font-semibold">{rupiah(it.total)}</td>
                  {isArang && <td className="p-3 text-right font-mono hide-mobile text-rose-600">{rupiah(it.sisa_tagihan)}</td>}
                  <td className="p-3 hide-mobile">
                    <span className={statusBadge(it.status_pembayaran)}>{it.status_pembayaran || "lunas"}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button title="Invoice PDF" onClick={()=>openInvoice(it.id)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`inv-pdf-${it.id}`}><FileText size={14} className="text-slate-600"/></button>
                      <button title="Kirim WA" onClick={()=>sendWA(it)} className="p-1.5 rounded-sm hover:bg-emerald-50"><MessageCircle size={14} className="text-emerald-600"/></button>
                      <button title="Kirim Email" onClick={()=>sendEmail(it)} className="p-1.5 rounded-sm hover:bg-blue-50"><Send size={14} className="brand-text"/></button>
                      <button title="Ubah" onClick={()=>openEdit(it)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-${it.id}`}><Pencil size={14} className="text-slate-600"/></button>
                      <button title="Hapus" onClick={()=>delOne(it.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-${it.id}`}><Trash2 size={14} className="text-rose-600"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={totalRows} pageSize={pageSize} testid="pemasukan-pagination"/>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="pemasukan-dialog">
          <DialogHeader><DialogTitle>{editId?"Ubah":"Tambah"} Pemasukan {isArang && "(Arang)"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} data-testid="f-tanggal"/></div>
            <div className="space-y-1.5">
              <Label>No Invoice <span className="text-rose-500">*</span></Label>
              <div className="flex">
                <span className="inline-flex items-center px-3 border border-r-0 border-slate-200 bg-slate-50 font-mono text-sm">INV-HN-</span>
                <Input value={form.no_invoice_num} onChange={upd("no_invoice_num")} data-testid="f-inv" className="rounded-l-none"/>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Kategori</Label>
              <Select value={form.kategori_id} onValueChange={(v)=>{const k=kategori.find(x=>x.id===v);setForm({...form,kategori_id:v,kategori_nama:k?.nama||""});}}>
                <SelectTrigger data-testid="f-kategori"><SelectValue placeholder="Pilih kategori"/></SelectTrigger>
                <SelectContent>{kategori.map(k=><SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>{isArang ? "Deskripsi (mis. Arang Batok Premium)" : "Nama Barang / Jasa"} <span className="text-rose-500">*</span></Label><Input value={form.nama_barang} onChange={upd("nama_barang")} data-testid="f-barang"/></div>
            {canInternal && (
              <div className="md:col-span-2 space-y-1.5">
                <Label>Dijual Ke</Label>
                <Select value={form.ke_pabrik_id || "luar"} onValueChange={(v)=>{
                  const t = targets.find(x=>x.id===v);
                  setForm({ ...form, ke_pabrik_id: t ? t.id : null, nama_pembeli: t ? t.nama : (form.ke_pabrik_id ? "" : form.nama_pembeli), status_pembayaran: t ? "lunas" : form.status_pembayaran });
                }}>
                  <SelectTrigger data-testid="pmk-ke-pabrik"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="luar">Pembeli luar</SelectItem>
                    {targets.map((t)=><SelectItem key={t.id} value={t.id}>Internal → {t.nama} (bahan baku briket)</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.ke_pabrik_id && <div className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1.5" data-testid="pmk-internal-info">Otomatis tercatat sebagai Bahan Masuk + Pengeluaran di pabrik briket. Di laporan Gabungan tidak dihitung sebagai omzet.</div>}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Nama Pembeli <span className="text-rose-500">*</span></Label>
              <Input list="pembeli-list" readOnly={!!form.ke_pabrik_id} className={form.ke_pabrik_id ? "bg-violet-50 text-violet-800 font-medium" : ""} value={form.nama_pembeli} onChange={(e)=>{
                const val=e.target.value; const p=pembeli.find(x=>x.nama===val);
                setForm({...form, nama_pembeli:val, no_wa_pembeli:p?.no_wa||form.no_wa_pembeli, email_pembeli:p?.email||form.email_pembeli});
              }} data-testid="f-pembeli"/>
              <datalist id="pembeli-list">{pembeli.map(p=><option key={p.id} value={p.nama}/>)}</datalist>
            </div>
            <div className="space-y-1.5"><Label>No. WhatsApp</Label><Input value={form.no_wa_pembeli} onChange={upd("no_wa_pembeli")} data-testid="f-wa"/></div>
            <div className="space-y-1.5"><Label>Email Pembeli</Label><Input type="email" value={form.email_pembeli} onChange={upd("email_pembeli")} data-testid="f-email"/></div>

            {isArang ? (
              <>
                <div className="md:col-span-2 space-y-1.5">
                  <Label>Produk Dijual <span className="text-rose-500">*</span></Label>
                  <Select value={form.produk_id || produkList[0]?.id || ""} onValueChange={(v)=>setForm({ ...form, produk_id: v })}>
                    <SelectTrigger data-testid="pmk-produk"><SelectValue placeholder="Pilih produk"/></SelectTrigger>
                    <SelectContent>{produkList.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Kg Terjual <span className="text-rose-500">*</span></Label><Input type="number" min="0" step="0.01" value={form.kg_terjual} onChange={upd("kg_terjual")} data-testid="f-kg"/></div>
                <div className="space-y-1.5"><Label>Harga per Kg (Rp) <span className="text-rose-500">*</span></Label><CurrencyInput value={form.harga_per_kg} onValueChange={upd("harga_per_kg")} data-testid="f-hargakg"/></div>
                <div className="space-y-1.5">
                  <Label>Subtotal (Kg × Harga/Kg)</Label>
                  <div className="h-9 px-3 flex items-center border border-slate-200 bg-slate-50 font-mono">{rupiah(baseHarga)}</div>
                </div>
                <div className="space-y-1.5">
                  <Label>Status Pembayaran</Label>
                  <Select value={form.status_pembayaran} disabled={!!form.ke_pabrik_id} onValueChange={(v)=>setForm({...form,status_pembayaran:v})}>
                    <SelectTrigger data-testid="f-status"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lunas">Lunas</SelectItem>
                      <SelectItem value="dp">DP (Uang Muka)</SelectItem>
                      <SelectItem value="belum">Belum Bayar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.status_pembayaran === "dp" && (
                  <div className="space-y-1.5"><Label>Jumlah DP (Rp)</Label><CurrencyInput value={form.dp_amount} onValueChange={upd("dp_amount")} data-testid="f-dp"/></div>
                )}
              </>
            ) : (
              <>
                <div className="space-y-1.5"><Label>Harga (Rp) <span className="text-rose-500">*</span></Label><CurrencyInput value={form.harga} onValueChange={upd("harga")} data-testid="f-harga"/></div>
                <div className="space-y-1.5">
                  <Label>Status Pembayaran</Label>
                  <Select value={form.status_pembayaran} onValueChange={(v)=>setForm({...form,status_pembayaran:v})}>
                    <SelectTrigger data-testid="f-status"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="lunas">Lunas</SelectItem><SelectItem value="piutang">Piutang</SelectItem></SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-1.5"><Label>Diskon (%)</Label><Input type="number" min="0" max="100" value={form.diskon_persen} onChange={upd("diskon_persen")} data-testid="f-diskon"/></div>
            <div className="space-y-1.5"><Label>{settings.is_pkp ? `PPN (${settings.ppn_persen}%)` : "Pajak (%)"}</Label><Input type="number" min="0" max="100" value={form.pajak_persen} onChange={upd("pajak_persen")} data-testid="f-pajak"/></div>

            <div className="space-y-1.5">
              <Label>Total</Label>
              <div className="h-9 px-3 flex items-center border border-slate-200 bg-slate-50 font-mono text-slate-900" data-testid="f-total">{rupiah(total)}</div>
            </div>
            {isArang && (
              <div className="space-y-1.5">
                <Label>Sisa Tagihan</Label>
                <div className="h-9 px-3 flex items-center border border-slate-200 bg-rose-50 font-mono text-rose-700" data-testid="f-sisa">{rupiah(sisaTagihan)}</div>
              </div>
            )}
<AkunSelect className="col-span-1 md:col-span-2" label="Diterima ke akun (uang masuk)" value={form.akun_id} onChange={(v) => setForm((f) => ({ ...f, akun_id: v }))} testid="f-akun"/>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} data-testid="f-keterangan"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Batal</Button>
            <Button onClick={save} className="brand-btn rounded-sm" data-testid="pemasukan-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
